import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, MapPin, Users, Music, Drum, Download, ExternalLink, ChevronRight, Star, Sparkles, Flame, Crown, Zap, HeartHandshake } from 'lucide-react';
import { PageEditProvider } from '@/context/PageEditModeContext';
import { PageEditable } from '@/components/edit/PageEditable';
import { PageEditableImage } from '@/components/edit/PageEditableImage';
import { useHero } from '@/hooks/useHero';
import { PageEditToolbar } from '@/components/edit/PageEditToolbar';
import { usePageEditMode } from '@/context/PageEditModeContext';

/**
 * Mardi Gras Brasil Carnaval Conference 2027 - Registration page
 *
 * Content is a direct migration from https://www.casasamba.com/Registration/
 * The user owns that content and asked for its wording to be preserved
 * verbatim.  Do NOT rewrite copy — treat the strings below as authored
 * material to be edited only by the user through the future admin editor.
 */

// ---------- Hero background photos ----------
const HERO_PHOTOS = [
  'https://www.casasamba.com/wp-content/uploads/2026/09/1hm9k73e_carol-endymion-2o25.jpg',
];

const useHeroPhotoRotation = () => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % HERO_PHOTOS.length), 4500);
    return () => clearInterval(t);
  }, []);
  return idx;
};

// ---------- Countdown to Feb 1, 2027 ----------
const CONFERENCE_START = new Date('2027-02-01T10:00:00-06:00');

const useCountdown = () => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, CONFERENCE_START - now);
  const days = Math.floor(diff / 86400000);
  const hrs = Math.floor((diff / 3600000) % 24);
  const min = Math.floor((diff / 60000) % 60);
  const sec = Math.floor((diff / 1000) % 60);
  return { days, hrs, min, sec };
};

// ---------- Data: 6-day schedule ----------
const SCHEDULE_DAYS = [
  {
    key: 'day-1-monday',
    label: 'Day 1, Monday February 1, 2027',
    sub: 'Monday 10 AM to 5 PM registration and check in\nMonday night performance 6 to 9',
  },
  {
    key: 'day-2-tuesday',
    label: 'Day 2, Tuesday February 2',
    sub: 'Tuesday 10 a.m. to 5 p.m. Audition and rehearsal session\nTuesday night 6 to 9 PM rehearsal session',
  },
  {
    key: 'day-3-wednesday',
    label: 'Day 3, February 3',
    sub: 'Wednesday 10 a.m. to 5 p.m. Audition and rehearsal session\nWednesday night 6 to 9 PM rehearsal session.',
  },
  {
    key: 'day-4-thursday',
    label: 'Day 4, February 4',
    sub: 'Thursday 10 a.m. to 5 p.m. Audition and mandatory rehearsal session\nThursday night 6 to 9 PM rehearsal session',
  },
  {
    key: 'day-5-friday',
    label: 'Day 5, Friday February 5',
    sub: 'Mandatory dress rehearsal session 10 AM to 5 PM parade orientation meeting to distribute costumes, information on the meeting location for the parade and credentials and final rehearsal.\nFriday night 6 to 9 PM rehearsal session',
  },
  {
    key: 'day-6-saturday',
    label: 'Day 6, Saturday February 6',
    sub: 'Saturday - 10:00 A.M. meet at 800 Race St. The annunciation center and we will catch a bus to the designated parade location Marconi Drive and city Park Avenue. Expected arrival there is 2:30 PM',
  },
];

// ---------- Data: pricing sections ----------
const PRICING = [
  {
    title: 'Professional Samba dancers',
    price: '$75.00',
    bullets: [
      'Open position Limited to 25 professional level samba dancers',
      'Friday the 5th is a mandatory rehearsal for everyone. All registered Guest are required to complete (2)two rehearsals in order to participate in the parade',
      'Juice, Coffee/Snacks during the break',
      'Printed Info Metarials',
      '4 Day Workshop',
      'registration includes Free passes for nights performances',
    ],
  },
  {
    title: 'The next section is the zoo animal section',
    price: '$100',
    bullets: [
      'Open position it will be six groups of animal sections each group will be limited to 35 people per section',
      'Friday the 5th is a mandatory rehearsal for everyone. All registered Guest are required to complete (2)two rehearsals in order to participate in the parade',
      'Free Coffee/Snacks during the break',
      'Printed Info Metarials',
      '4 Day Workshop',
      'registration includes Free passes for nights performances',
    ],
  },
  {
    title: 'The next section is the zoo animal sectio for Students',
    price: '$75',
    bullets: [
      'Open position it will be six groups of animal sections each group will be limited to 35 people per section',
      'Friday the 5th is a mandatory rehearsal for everyone. All registered Guest are required to complete (2)two rehearsals in order to participate in the parade',
      'Free Coffee/Snacks during the break',
      'Printed Info Metarials',
      '4 Day Workshop',
      'registration includes Free passes for nights performances',
    ],
  },
  {
    title: 'Non professional Samba dancers',
    price: '$100',
    bullets: [
      'Open position Limited to 25 Non-professional level samba dancers',
      'Friday the 5th is a mandatory rehearsal for everyone. All registered Guest are required to complete (2)two rehearsals in order to participate in the parade',
      'Free Coffee/Snacks during the break',
      'Printed Info Metarials',
      'NEW ! 4 Days and 4 Nights Workshop',
      'registration includes Free passes for nights performance',
    ],
  },
  {
    title: "Casa Samba's Directors and staff section (only pre-approval by Mestre Curtis Pierre)",
    price: '$0.00',
    bullets: [
      'Open position',
      'Friday the 4 and 5th is a mandatory rehearsal for everyone. All registered Guest are required to complete (2)two rehearsals in order to participate in the parade',
      'Free Coffee/Snacks during the break',
      'Printed Info Metarials',
      '4 Day Workshop',
      'registration includes Free passes for nights performances',
    ],
  },
  {
    title: 'Bateria (Samba Drummers)',
    price: '$350',
    featured: true,
    bullets: [
      'Open position Limited to 60 Samba drummers you will be placed in the Bateria according to your skill level',
      'Friday the 5th is a mandatory rehearsal for everyone. All registered Guest are required to complete (2)two rehearsals in order to participate in the parade',
      'Free Coffee/Snacks during the break',
      'Printed Info Metarials',
      '4 Day Workshop',
      'registration includes Free passes for nights performances',
      'You be able to take your costume Home',
    ],
  },
  {
    title: 'Pay Per Day and Night Bateria (Samba Drummers) minimum requirement of 2 Days',
    price: '$70',
    bullets: [
      'Open position Limited to 60 Samba drummers you will be placed in the Bateria according to your skill level',
      'Friday the 4 and 5th is a mandatory rehearsal for everyone in the Bateria. All registered Guest are required to complete (2)two rehearsals in order to participate in the parade',
      'Free Coffee/Snacks during the break',
      'Printed Info Metarials',
      '4 Night Dumming Workshop',
      'registration includes Free passes for nights performances',
      'You be able to take your costume Home',
    ],
  },
  {
    title: 'Pay Per Day and Night for Passistas (Samba Dancers) minimum requirement of 2 Days',
    price: '$30',
    bullets: [
      'Open position Limited to 60 Samba drummers you will be placed in the Bateria according to your skill level',
      'Friday the 4 and 5th is a mandatory rehearsal for everyone in the Bateria. All registered Guest are required to complete (2)two rehearsals in order to participate in the parade',
      'Free Coffee/Snacks during the break',
      'Printed Info Metarials',
      '4 Night Dumming Workshop',
      'registration includes Free passes for nights performances',
      'You be able to take your costume Home',
    ],
  },
];

// ---------- Data: testimonials ----------
const TESTIMONIALS = [
  {
    quote: 'Experience the Magic: Join the First-Ever New Orleans Mardi Gras Brazil Carnaval Conference and Parade!',
    name: 'Dana Leon',
    role: 'Afro-Brazilian Dancer, Fashion designer, and performer',
    photo: '/api/media/casa-samba/library/images/carnaval/our_staff_2.jpg',
  },
  {
    quote: "I'm proud to be one of the sponsors the inaugural New Orleans Mardi Gras Brazil Carnaval conference, offering five days of training leading up to the spectacular Endymion Parade. Join Casa Samba for this historic event and seize the priceless opportunity to participate in the largest parade in New Orleans. While watching the parade is an incredible experience, being in the parade is truly a once-in-a-lifetime opportunity. Don't miss out - you have to be there! Experience the excitement by participating in the New Orleans Mardi Gras Brazil Carnaval Conference!",
    name: 'Tyrone Henry',
    role: 'Founder and creator of Bissap Brezze LLC.',
    photo: '/api/media/casa-samba/library/images/carnaval/tee.jpg',
  },
  {
    quote: 'Experience the Excitement: Join the First-Ever New Orleans Mardi Gras Brazil Carnaval Conference and Parade with Escola de Samba Casa Samba! I can\'t wait to do this.',
    name: 'Roberta Santiago',
    role: 'SEO Manager UPS New York',
    photo: '/api/media/casa-samba/library/images/carnaval/member-3.jpg',
  },
  {
    quote: 'I always wanted to go to New Orleans, but never really had the time I am also a retired drummer. But my drumming chops are still in good standings. I am definitely going to New Orleans and be a part of this epic event',
    name: 'Alex Gomez',
    role: 'Vice President Company Limousines to Go LLC.',
    photo: '/api/media/casa-samba/library/images/carnaval/member-4.jpg',
  },
];

// ---------- Data: sponsors ----------
const SPONSORS = [
  { name: 'NORD', src: '/api/media/casa-samba/library/images/carnaval/nord-logo.jpg' },
  { name: 'Salgueiro', src: '/api/media/casa-samba/library/images/carnaval/Salgueiro-Logo.png' },
  { name: 'Casa Samba', src: '/api/media/casa-samba/library/images/carnaval/Casa-Samba-Logo-2025-version1-2.png' },
  { name: 'ETC', src: '/api/media/casa-samba/library/images/carnaval/ETC-LOGO-no-virtual-.png' },
  { name: 'Arts Council', src: '/api/media/casa-samba/library/images/carnaval/arts-council-logo.png' },
  { name: 'AWS', src: '/api/media/casa-samba/library/images/carnaval/aws_logo.png' },
];

const REGISTER_URL = 'https://www.casasamba.com/Registration/registration/';
const SCHEDULE_PDF = '/docs/Mardi-Gras-Brasil-Conference-schedule-2027.pdf';
const ANIMAL_SECTION_URL = 'https://www.casasamba.com/Registration/animal-section-description/';

// ---------- Sub-components ----------
const CountdownBox = ({ value, label }) => (
  <div className="flex flex-col items-center px-4 py-3 rounded-xl bg-black/40 border border-primary/40 min-w-[80px]">
    <div className="text-3xl md:text-4xl font-bold font-display text-primary tabular-nums">
      {String(value).padStart(2, '0')}
    </div>
    <div className="text-xs uppercase tracking-wider text-muted-foreground mt-1">{label}</div>
  </div>
);

const SectionHeading = ({ children, className = '' }) => (
  <h2 className={`text-3xl md:text-4xl font-bold font-display text-foreground mb-6 ${className}`}>
    {children}
  </h2>
);

// ---------- Main page ----------
const Registration = () => {
  const hero = useHero('registration');
  return (
    <PageEditProvider slug="registration">
      <RegistrationContent />
      <PageEditToolbar />
    </PageEditProvider>
  );
};

const RegistrationContent = () => {
  const { days, hrs, min, sec } = useCountdown();
  const [activeDay, setActiveDay] = useState(SCHEDULE_DAYS[0].key);
  const heroPhotoIdx = useHeroPhotoRotation();
  const { enabled: editEnabled, getValue } = usePageEditMode();
  const heroBg = getValue('hero.backgroundImage') || HERO_PHOTOS[0];

  return (
    <div className="min-h-screen bg-background text-foreground pb-24 md:pb-0" data-testid="registration-page">
      {/* Hero */}
      <section className="relative overflow-hidden">
        {/* Hero background photo (editable) */}
        <div className="absolute inset-0" aria-hidden>
          {editEnabled ? (
            <div className="absolute inset-0">
              <PageEditableImage
                path="hero.backgroundImage"
                fallback={HERO_PHOTOS[0]}
                alt="Hero background"
                className="w-full h-full object-cover"
              />
            </div>
          ) : (
            <motion.div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${heroBg})` }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.4, ease: 'easeInOut' }}
            />
          )}
          {/* Dark overlay so text is readable */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/80 pointer-events-none" />
          <div className="absolute inset-0 bg-gradient-to-br from-orange-900/50 via-red-900/40 to-yellow-900/40 mix-blend-multiply pointer-events-none" />
        </div>

        <motion.div
          className="absolute inset-0 opacity-40 pointer-events-none"
          aria-hidden
          animate={{ opacity: [0.3, 0.55, 0.3] }}
          transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          style={{
            background:
              'radial-gradient(circle at 20% 30%, rgba(255,140,0,0.35), transparent 40%), radial-gradient(circle at 80% 70%, rgba(220,20,60,0.35), transparent 40%), radial-gradient(circle at 50% 100%, rgba(255,215,0,0.25), transparent 45%)',
          }}
        />
        {/* Confetti-style sparkles */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden>
          {[...Array(18)].map((_, i) => (
            <motion.span
              key={i}
              className="absolute w-1.5 h-1.5 rounded-full bg-yellow-300"
              style={{
                top: `${(i * 37) % 100}%`,
                left: `${(i * 53) % 100}%`,
                filter: 'blur(0.5px)',
              }}
              animate={{
                y: [-6, 8, -6],
                opacity: [0.15, 0.9, 0.15],
              }}
              transition={{
                duration: 3 + (i % 4),
                repeat: Infinity,
                delay: i * 0.15,
                ease: 'easeInOut',
              }}
            />
          ))}
        </div>

        <div className="relative max-w-6xl mx-auto px-6 py-20 md:py-28">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <motion.div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/20 border border-primary/50 mb-6"
              data-testid="hero-badge"
              animate={{ boxShadow: ['0 0 0 0 rgba(251,146,60,0.5)', '0 0 0 10px rgba(251,146,60,0)'] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <Sparkles className="w-4 h-4 text-primary" />
              <PageEditable
                path="hero.badge"
                as="span"
                fallback="Escola de Samba Casa Samba presents"
                className="text-primary text-sm font-semibold uppercase tracking-wider"
              />
            </motion.div>

            <PageEditable
              path="hero.title"
              as="h1"
              fallback="Annual Mardi Gras Brazil Carnaval Conference 2027"
              className="text-4xl sm:text-5xl lg:text-6xl font-bold font-display mb-4 leading-tight"
              data-testid="hero-title"
            />
            <PageEditable
              path="hero.subtitle"
              as="p"
              multiline
              fallback="Six days. One unforgettable parade. Train with master drummers, wear a handmade Carnaval costume, and march down the streets of New Orleans in the biggest parade of the season — Endymion."
              className="text-lg md:text-xl text-muted-foreground max-w-3xl mb-8"
            />

            <div className="mb-8">
              <PageEditable
                path="hero.countdownLabel"
                as="p"
                fallback="Conference Starts in:"
                className="text-sm uppercase tracking-wider text-muted-foreground mb-3"
              />
              <div className="flex flex-wrap gap-3" data-testid="countdown">
                <CountdownBox value={days} label="Days" />
                <CountdownBox value={hrs} label="Hrs" />
                <CountdownBox value={min} label="Min" />
                <CountdownBox value={sec} label="Sec" />
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <motion.a
                href="#register-form"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gradient-to-r from-orange-500 via-red-500 to-yellow-500 text-white font-bold shadow-lg shadow-orange-500/40 hover:scale-105 transition-transform"
                data-testid="hero-register-btn"
                animate={{
                  boxShadow: [
                    '0 0 0 0 rgba(251,146,60,0.55)',
                    '0 0 0 14px rgba(251,146,60,0)',
                  ],
                }}
                transition={{ duration: 1.8, repeat: Infinity, ease: 'easeOut' }}
              >
                <Flame className="w-4 h-4" />
                <PageEditable path="hero.registerButton" as="span" fallback="Claim my parade spot" />{' '}
                <ChevronRight className="w-4 h-4" />
              </motion.a>
              <a
                href={REGISTER_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-primary text-primary hover:bg-primary/10 transition-colors"
                data-testid="hero-reserve-btn"
              >
                <PageEditable path="hero.reserveButton" as="span" fallback="Reserve My Seat" />
              </a>
              <a
                href={SCHEDULE_PDF}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-border text-foreground hover:bg-card transition-colors"
                data-testid="hero-download-schedule"
              >
                <Download className="w-4 h-4" />{' '}
                <PageEditable path="hero.downloadButton" as="span" fallback="Download Schedule" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Urgency banner */}
      <section className="max-w-6xl mx-auto px-6 pt-8">
        <div className="rounded-2xl bg-gradient-to-r from-red-600/20 via-orange-600/20 to-yellow-600/20 border border-orange-500/40 p-4 md:p-5 flex flex-col md:flex-row items-center gap-3 md:gap-6 text-center md:text-left">
          <div className="w-10 h-10 rounded-full bg-orange-500/30 border border-orange-500/60 flex items-center justify-center flex-shrink-0">
            <Zap className="w-5 h-5 text-orange-300" />
          </div>
          <div className="flex-1">
            <PageEditable
              path="urgency.heading"
              as="p"
              fallback="Only 300 parade spots — 60 Bateria, 50 dancers, 200 animal-section"
              className="font-semibold text-foreground"
            />
            <PageEditable
              path="urgency.sub"
              as="p"
              fallback="Once spots are gone, the parade is closed for the year. Lock yours in before rehearsals begin."
              className="text-sm text-muted-foreground mt-1"
            />
          </div>
          <a
            href="#register-form"
            className="px-5 py-2.5 rounded-full bg-primary text-black font-bold hover:opacity-90 transition-opacity whitespace-nowrap"
            data-testid="urgency-cta"
          >
            <PageEditable path="urgency.cta" as="span" fallback="Save my spot" />
          </a>
        </div>
      </section>

      {/* What you'll experience */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <PageEditable
          path="experience.eyebrow"
          as="p"
          fallback="What you'll walk away with"
          className="text-primary text-sm font-semibold uppercase tracking-wider mb-3"
        />
        <SectionHeading>
          <PageEditable path="experience.heading" as="span" multiline fallback="Six days that turn drummers, dancers & dreamers into a Carnaval school." />
        </SectionHeading>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-6">
          {[
            { icon: Drum,          key: 'drums',    title: 'Play the rhythms',   desc: 'Sit in the Bateria under Mestre Curtis and his directors — Surdo, Repinique, Caixa, Tamborim, Agogo.' },
            { icon: Crown,         key: 'costume',  title: 'Wear a handmade costume', desc: 'Full Carnaval regalia crafted by Casa Samba — the Bateria costume is yours to keep.' },
            { icon: Star,          key: 'parade',   title: 'March in Endymion',  desc: 'Take the biggest parade in New Orleans, side by side with Mestre Curtis and the whole school.' },
            { icon: HeartHandshake,key: 'community',title: 'Join the family',   desc: 'Meet dancers and drummers from around the world. You leave with a school, not just a memory.' },
          ].map((tile, i) => (
            <motion.div
              key={tile.key}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08, duration: 0.4 }}
              className="rounded-2xl bg-card border border-border/60 p-5 hover:border-primary/60 transition-colors group"
            >
              <div className="w-11 h-11 rounded-full bg-gradient-to-br from-orange-500/30 to-red-500/30 border border-orange-500/40 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <tile.icon className="w-5 h-5 text-orange-300" />
              </div>
              <PageEditable
                path={`experience.${tile.key}.title`}
                as="h3"
                fallback={tile.title}
                className="text-lg font-bold text-foreground mb-2"
              />
              <PageEditable
                path={`experience.${tile.key}.desc`}
                as="p"
                multiline
                fallback={tile.desc}
                className="text-sm text-muted-foreground leading-relaxed"
              />
            </motion.div>
          ))}
        </div>
      </section>

      {/* JotForm Registration */}
      <section id="register-form" className="max-w-6xl mx-auto px-6 py-16 scroll-mt-24">
        <div className="rounded-2xl bg-card border-2 border-orange-500/40 p-4 md:p-8 shadow-2xl shadow-orange-500/10">
          <PageEditable
            path="form.eyebrow"
            as="p"
            fallback="Complete your registration below"
            className="text-primary text-sm font-semibold uppercase tracking-wider mb-2 text-center"
          />
          <SectionHeading className="text-center mb-2">
            <PageEditable
              path="form.heading"
              as="span"
              fallback="Register for Mardi Gras Brasil Carnaval Parade with Casa Samba"
            />
          </SectionHeading>
          <PageEditable
            path="form.subheading"
            as="p"
            fallback="Fill out the form to reserve your spot in the parade. All payments are handled securely by JotForm."
            className="text-muted-foreground text-center mb-6"
          />
          <div className="rounded-xl overflow-hidden bg-white">
            <iframe
              id="JotFormIFrame-253024827567159"
              title="Mardi Gras Brasil CARNAVAL Parade with CASA SAMBA"
              src="https://pci.jotform.com/form/253024827567159"
              frameBorder="0"
              scrolling="no"
              style={{ minWidth: '100%', maxWidth: '100%', height: '900px', border: 'none' }}
              allow="geolocation; microphone; camera; payment"
              data-testid="jotform-iframe"
            />
          </div>
          <p className="text-xs text-muted-foreground text-center mt-4">
            Having trouble with the form?{' '}
            <a
              href="https://pci.jotform.com/form/253024827567159"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              Open it in a new tab
            </a>.
          </p>
        </div>
      </section>

      {/* Why attend */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <SectionHeading>
          <PageEditable path="whyAttend.heading" as="span" multiline fallback="Why Attend Mardi Gras Brazil Carnaval Conference 2027?" />
        </SectionHeading>
        <div className="prose prose-invert max-w-none space-y-6 text-muted-foreground leading-relaxed">
          <PageEditable
            path="whyAttend.p1"
            as="p"
            multiline
            className="font-semibold text-foreground text-lg"
            fallback="Join us at the inaugural New Orleans Mardi Gras Brazil Carnaval conference, sponsored by Escola de Samba Casa Samba! Get ready for four days of training leading up to the epic Endymion Parade, the biggest parade in New Orleans. Don't miss this priceless opportunity to be part of history and experience the magic of the parade from the inside. It's an experience of a lifetime!"
          />
          <PageEditable
            path="whyAttend.p2"
            as="p"
            multiline
            fallback="Along with this lifetime experience, you will be helping to restart the NORDC and Casa Samba Community children's program called the Samba Kids www.sambakids.com. The program has been around for more than 20 years, with the proceeds from the registration for this Carnaval conference in 2027. Casa Samba will be able to build our children's program back up to doing the festivals and performances around the city representing Youth involved in Afro-Brazilian culture."
          />
          <PageEditable
            path="whyAttend.p3"
            as="p"
            multiline
            fallback="Description: The Conference will take place from February 1 to February 6th. Rehearsals will be held from Monday to Friday, with two sessions per day from 10 AM to 12 PM and 3:15 PM to 5 PM. Additionally, there will be two mandatory rehearsals on Thursday and Friday. On Friday, costume distribution will take place for the percussion band (Bateria), and a dress rehearsal for all participants, including individual animal sections. Important instructions regarding meeting points and items allowed at the parade will also be provided."
          />
          <PageEditable
            path="whyAttend.p4"
            as="p"
            multiline
            fallback="Using Afro-Brazilian rhythms and dance of Carnaval in Rio and Bahia, a mixture of both. Plus, this is a melting pot city, we have to include the Oxossi rhythms in this Carnaval 2027 mixture of Brazil and New Orleans rhythms."
          />

          <ul className="space-y-2 list-disc pl-6">
            <li><PageEditable path="whyAttend.li1" as="span" multiline fallback="Check-in - Monday 10 AM to 5 PM for registration and check-in" /></li>
            <li><PageEditable path="whyAttend.li2" as="span" multiline fallback="Rehearsals - Monday - Friday General rehearsals between 10 to 5 PM, including a 30-minute snack break" /></li>
            <li><PageEditable path="whyAttend.li3" as="span" multiline fallback="Night rehearsals - Tuesday through Friday for dancers and drummers." /></li>
            <li><PageEditable path="whyAttend.li4" as="span" multiline fallback="Costume fitting - Thursday the 12th and Friday the 5th for inspection, distribution of costumes, and information and credentials" /></li>
            <li><PageEditable path="whyAttend.li5" as="span" multiline fallback="Endymion Parade - Saturday, February 6th, the big day in the big easy! This is the day of the parade. We meet the Bus at the Annunciation Center, 800 Race for 10A.M. The Location the Parde will start is at Marconi Drive and city Park Avenue in City Park" /></li>
          </ul>
        </div>
      </section>

      {/* Experts */}
      <section className="max-w-5xl mx-auto px-6 py-12">
        <SectionHeading>
          <PageEditable path="experts.heading" as="span" multiline fallback="Quality Time with both New Orleans and Brasilian Experts on Culture" />
        </SectionHeading>
        <PageEditable
          path="experts.body"
          as="p"
          multiline
          className="text-muted-foreground leading-relaxed"
          fallback="World-renowned Master jazz drummer Jason Marsalis, the incomparable James Andrews, one of the greatest premier trumpeters, touring the world representing New Orleans, we are expecting guests surprise appearance from various artists from the city of culture. Because everyone is extremely busy during Mardi Gras time Big Chief Shaka Zulu, Big Chief Honey, two of the pioneers of the Mardi Gras Black Indian culture, YMO Young in Olympia social aid and pleasure club (Second line dancers), and New Orleans ambassador of Afro-Brasilian culture Mestre Curtis. Mestre will speak about the new cultural update on the Street rhythms of New Orleans. Due to his being born and raised in New Orleans rhythms and submerging his life into Brazilian culture rhythms, he holds the most accurate knowledge, therefore putting himself at the Crossroads of Carnaval. Come here to his story of Drumming in New Orleans."
        />
      </section>

      {/* Who should attend */}
      <section className="max-w-5xl mx-auto px-6 py-12">
        <SectionHeading>
          <PageEditable path="whoShould.heading" as="span" fallback="Who Should Attend?" />
        </SectionHeading>
        <PageEditable
          path="whoShould.intro"
          as="p"
          multiline
          className="text-muted-foreground leading-relaxed mb-6"
          fallback="Anyone can be a part of this great spectacular event. Even if you are 14 years and older you can join with proof of age. If you are a professional or semi-professional dancer or musician/drummer. You would be eligible to be in any of the following sections. Section 1 Drummers(Bateria) Section 2 Dancer (Passistas) Section 3 Zoo animals. If you are a non-professional and want to have fun, we suggest you join section 3 the Zoo Animals section."
        />

        <div className="bg-card border border-border/50 rounded-2xl p-6 mb-6">
          <PageEditable path="whoShould.card1.time" as="div" className="font-bold text-foreground mb-2" fallback="10:00 AM to 5:00 PM" />
          <PageEditable path="whoShould.card1.title" as="div" className="font-semibold text-primary mb-3" fallback="Registration check in auditions" />
          <PageEditable path="whoShould.card1.p1" as="p" multiline className="text-muted-foreground text-sm leading-relaxed mb-4" fallback="Complementary fruit and snacks and registration for Mardi Gras Brazil Carnaval Conference 2027 Pick up your name tag, and credentials and schedule the week's activities. Drum and dance auditions for Samba Skill-level casting." />
          <PageEditable path="whoShould.card1.p2" as="p" multiline className="text-muted-foreground text-sm leading-relaxed mb-4" fallback="from 10am to 12pm, introduction of the Samba instrument rehearsal session. The introduction rehearsal section is for everyone who is considered a beginner player. The introductory of instruments session will be on the percussion section, Plattinella, Tamborim, Reco-reco, Cuica, and Agogo(Agogo quartro boca), and then the drum section. Repequie and Caixa section, Repequie de More, and a Surdo section." />
          <PageEditable path="whoShould.card1.p3" as="p" multiline className="text-muted-foreground text-sm leading-relaxed" fallback="The daytime 12:15 pm to 5 pm section is for everyone, Bateria (drummers) and Passistas (dancers). The drummers will be assigned to their instrument sections. Dancers will be divided into three groups: Samba Dancer sections A, B, and C. Section A will represent the professional Samba dancers." />
        </div>

        <div className="bg-card border border-border/50 rounded-2xl p-6">
          <PageEditable path="whoShould.card2.title" as="div" className="font-bold text-foreground mb-2" fallback="Night Rehearsal Session 6 – 9 pm" />
          <PageEditable path="whoShould.card2.p1" as="p" multiline className="text-muted-foreground text-sm leading-relaxed mb-4" fallback="NOTE: There will be two rehearsal sessions. One during the day for out of town visitors and one during the night for local people who work during the day. You can also attend both sessions. The Group rehearsal will be from 12pm to 5pm and from 6 pm from 9 pm. There will be a 30-minute break between each rehearsal sessions. This will be an all-day section for everyone to rehearse together as a group, under the direction of Mestre Curtis Pierre and his Directors." />
          <PageEditable path="whoShould.card2.p2" as="p" multiline className="text-muted-foreground text-sm leading-relaxed" fallback="The daytime 12:15pm to 5pm and the night time sessions is From 6pm to 9pm the rehearsal section is for everyone Bateria (drummers) and Passistas (dancers). The drummers will be assigned to their instrument sections. Dancers will be divided into three groups: Samba Dancer sections A, B, and C. Section A will represent the professional Samba dancers. Section B will be the non-professional Samba dancers. Section C will represent the animal section, which will be divided into six groups according to the name of the animal costume they have. In the second half of rehearsal, everyone will rehearse as a group." />
        </div>
      </section>

      {/* Registration Open Callout */}
      <section className="max-w-5xl mx-auto px-6 py-12">
        <div className="rounded-2xl bg-gradient-to-r from-orange-500/20 via-red-500/20 to-yellow-500/20 border-2 border-orange-500/50 p-8">
          <PageEditable
            path="regOpen.heading"
            as="h3"
            multiline
            className="text-2xl md:text-3xl font-bold font-display text-foreground mb-4"
            fallback="Registration is now open for the biggest Mardi Gras celebration in the United States!"
          />
          <PageEditable
            path="regOpen.body"
            as="p"
            multiline
            className="text-muted-foreground text-lg leading-relaxed mb-6"
            fallback="Join us from February 1th to February 6, 2027, and experience the rhythms of Brazil with CASA Samba at the New Orleans Mardi Gras 2027. Don't miss out on the once-in-a-lifetime opportunity to be part of the epic Endymion Parade. Limited to 50 positions available as of Feburary 1, 2027, for Drummers (25 spots) and Dancers in section one (25 spots) and section two (50 spots). The animal section is still open."
          />

          <div className="rounded-xl overflow-hidden mb-6 border border-border/50">
            <PageEditableImage
              path="regOpen.image"
              fallback="/api/media/casa-samba/library/images/carnaval/Animal-Vitalighting-Heads-1024x768.jpg"
              alt="Casa Samba lighted animal puppet heads"
              className="w-full max-h-96 object-cover"
            />
          </div>

          <PageEditable
            path="regOpen.sub"
            as="p"
            multiline
            className="text-muted-foreground leading-relaxed mb-6"
            fallback="Casa Samba and Mestre Curtis are seeking volunteers to carry lighted animal puppet heads as part of our Enredo/theme, inspired by the zoo. This is more than just a song – it's a powerful message. If you're interested, please get in touch with us."
          />

          <div className="flex flex-wrap gap-3">
            <a href={REGISTER_URL} target="_blank" rel="noopener noreferrer"
              className="px-6 py-3 rounded-full bg-primary text-black font-bold hover:opacity-90 transition-opacity"
              data-testid="callout-register-btn">
              <PageEditable path="regOpen.btn1" as="span" fallback="Register now" />
            </a>
            <a href={REGISTER_URL} target="_blank" rel="noopener noreferrer"
              className="px-6 py-3 rounded-full border border-primary text-primary hover:bg-primary/10 transition-colors">
              <PageEditable path="regOpen.btn2" as="span" fallback="Reserve My Seat" />
            </a>
            <a href={ANIMAL_SECTION_URL} target="_blank" rel="noopener noreferrer"
              className="px-6 py-3 rounded-full border border-border text-foreground hover:bg-card transition-colors inline-flex items-center gap-2">
              <PageEditable path="regOpen.btn3" as="span" fallback="Link to animal costumes" /> <ExternalLink className="w-4 h-4" />
            </a>
            <a href={SCHEDULE_PDF} target="_blank" rel="noopener noreferrer"
              className="px-6 py-3 rounded-full border border-border text-foreground hover:bg-card transition-colors inline-flex items-center gap-2">
              <Download className="w-4 h-4" /> <PageEditable path="regOpen.btn4" as="span" fallback="Download Schedule" />
            </a>
          </div>
        </div>
      </section>

      {/* Schedule tabs */}
      <section className="max-w-6xl mx-auto px-6 py-12">
        <SectionHeading>
          <PageEditable path="scheduleTabs.heading" as="span" multiline fallback="Mardi Gras Brazil Carnaval Conference 2027 Schedule" />
        </SectionHeading>
        <PageEditable
          path="scheduleTabs.intro"
          as="p"
          multiline
          className="text-muted-foreground mb-6 leading-relaxed"
          fallback="A full schedule of Live Brazil and New Orleans cultural performances, demonstration sessions & dance and drumming workshops. On both the culture of New Orleans and the Brasilian Carnaval"
        />

        <a href={REGISTER_URL} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-primary text-black font-semibold hover:opacity-90 transition-opacity mb-8">
          <PageEditable path="scheduleTabs.goToForm" as="span" fallback="GO to Registration form" /> <ChevronRight className="w-4 h-4" />
        </a>

        {/* Day selector */}
        <div className="flex flex-wrap gap-2 mb-6" role="tablist">
          {SCHEDULE_DAYS.map((d) => (
            <button
              key={d.key}
              onClick={() => setActiveDay(d.key)}
              role="tab"
              aria-selected={activeDay === d.key}
              data-testid={`day-tab-${d.key}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                activeDay === d.key
                  ? 'bg-primary text-black'
                  : 'bg-card border border-border/50 text-muted-foreground hover:border-primary/50'
              }`}
            >
              <PageEditable path={`scheduleDays.${d.key}.label`} as="span" fallback={d.label} />
            </button>
          ))}
        </div>

        {/* Day content */}
        {SCHEDULE_DAYS.map((d) => (
          <div key={d.key} hidden={activeDay !== d.key} className="bg-card border border-border/50 rounded-2xl p-6">
            <PageEditable path={`scheduleDays.${d.key}.heading`} as="h3" multiline className="text-xl md:text-2xl font-bold text-foreground mb-2" fallback={d.label} />
            <PageEditable path={`scheduleDays.${d.key}.body`} as="p" multiline className="text-muted-foreground whitespace-pre-line" fallback={d.sub} />
          </div>
        ))}
      </section>

      {/* Detailed daily sections */}
      <section className="max-w-6xl mx-auto px-6 py-12 space-y-8">
        <SectionHeading>
          <PageEditable path="dailySchedule.heading" as="span" fallback="Detailed Daily Schedule" />
        </SectionHeading>

        {/* MONDAY */}
        <DailyBlock
          pathPrefix="day.mon1"
          time="10:00 AM - 5:00 PM"
          location="Annunciation Recreation Center 800 Race St. New Orleans Louisiana 70130"
          image="/api/media/casa-samba/library/images/carnaval/IMG_0550-scaled.jpg"
          imgAlt="Mestre Curtis Pierre and Staff"
          caption="Mestre Curtis Pierre and Staff — Auditions and rehearsals"
          heading="Monday 10:00 AM to 5:00 PM registration check in auditions"
          paragraphs={[
            "Complementary fruit and snacks and registration for Mardi Gras Brazil Carnaval Conference 2027. Pick up your name tag, and credentials and schedule the week's activities. Drum and dance auditions for Samba Skill-level casting.",
            "from 10am to 12pm, introduction of the Samba instrument rehearsal session. The introduction rehearsal section is for everyone who is considered a beginner player. The introductory of instruments session will be on the percussion section, Plattinella, Tamborim, Reco-reco, Cuica, and Agogo (Agogo quartro boca), and then the drum section. Repequie and Caixa section, Repequie de More, and a Surdo section.",
            "The daytime 12:15 pm to 5 pm section is for everyone, Bateria (drummers) and Passistas (dancers). The drummers will be assigned to their instrument sections. Dancers will be divided into three groups: Samba Dancer sections A, B, and C. Section A will represent the professional Samba dancers. Section B will be the non-professional Samba dancers. Section C will represent the animal section, which will be divided into six groups according to the name of the animal costume they have. In the second half of rehearsal, everyone will rehearse as a group.",
          ]}
        />

        <DailyBlock
          pathPrefix="day.mon2"
          time="7:00 PM - 8:30 PM"
          location="Annunciation Recreation Center 800 Race St. New Orleans Louisiana 70130"
          image="/api/media/casa-samba/library/images/carnaval/curtis-backgrd1-.png"
          imgAlt="Mestre Curtis Pierre with Guest New Orleans Percussionist"
          caption="Mestre Curtis Pierre with Guest New Orleans Percussionist — Auditions and rehearsals"
          heading="Monday Night 7:00PM to 9:00 PM EPIC Night of Percussion"
          paragraphs={[
            "Monday night, February 1",
            "7 PM to 9 PM",
            "Director of Casa Samba Bateria Bomani Pierre and Jason Marsalis world-renowned jazz musician, performer, and composer, 7:00 PM to 9:00 PM. Mestre Curtis Pierre presents an evening with Master Drummers of New Orleans. Possible other guest drummers New Orleans",
            "The Young Master drummer of New Orleans, Jason Marsalis, and Bomani Pierre. The master artists will present comparisons and similarities of the Afro-Brazilian rhythms to the rhythms of New Orleans Jazz and brass band drumming. Special guest Big Chief Shaka Zulu. Don't miss this cultural update performance and a great jam session after the performance.",
            "Mestre Curtis handmade Ancestral Percussion String Orchestra. Mestre Curtis Presents an EPIC Night of Percussion. A special Night of Percussion, Mestre Curts and his Sun Bomani showcase the New Orleans percussionist. In a way that has never been seen before, featuring some of the best Percussion performing artists on the upper level in this city. There will also be a percussion display of Mestre Curtis's handmade string percussion instruments. Instruments that he has created, both from traditional materials and natural materials found in his surroundings.",
          ]}
        />

        <DailyBlock
          pathPrefix="day.tue1"
          time="12:00 PM - 5:00 PM"
          location="Annunciation Recreation Center 800 Race St. New Orleans Louisiana 70130"
          image="/api/media/casa-samba/library/images/carnaval/Endymion-Parade_GOOGLEDOCS-pics_25.jpg"
          imgAlt="Mestre Curtis Pierre and Staff"
          caption="Mestre Curtis Pierre and Staff — Auditions and rehearsals"
          heading="Tuesday 10 A.M. to 4:00 P.M. Audition/Rehearsals"
          paragraphs={[
            "February 2",
            "Tuesday 10 A.M. to 5:00 P.M. rehearsal session. The rehearsal section is for everyone. The first session will be of individual sectionals: Samba Tamborim section, a Samba percussion section, Plattinella, Reco-reco, Cuica, and Agogo (Agogo quartro boca), Repequie and Caixa section, Repequie de More, and a Surdo section. Dancers will be divided into three groups: Samba Dancer sections A, B, and C. Section A will represent the professional Samba dancers. Section B will be the non-professional Samba dancers. Section C will represent the animal section, which will be divided into six groups according to the name of the animal costume they have. In the second half of rehearsal, everyone will rehearse as a group.",
          ]}
        />

        <DailyBlock
          pathPrefix="day.tue2"
          time="7:00 PM - 8:30 PM"
          location="Annunciation Recreation Center 800 Race St. New Orleans Louisiana 70130"
          image="/api/media/casa-samba/library/images/carnaval/Bomani-2025-brasil-.png"
          imgAlt="Bomani Pierre"
          caption="Bomani Pierre — Director of the Bateria"
          heading="Tuesday Night Rehearsal Session"
          paragraphs={[
            "NOTE: There will be two rehearsal sessions. One during the day for out of town visitors and one during the night for local people who work during the day. You can also attend both sessions. The Group rehearsal will be from 12pm to 5pm and from 6 pm from 9 pm. There will be a 30-minute break between each rehearsal sessions. This will be an all-day section for everyone to rehearse together as a group, under the direction of Mestre Curtis Pierre and his Directors.",
            "The daytime 12:15pm to 5pm and the night time sessions is From 6pm to 9pm the rehearsal section is for everyone Bateria (drummers) and Passistas (dancers). The drummers will be assigned to their instrument sections. Dancers will be divided into three groups: Samba Dancer sections A, B, and C. Section A will represent the professional Samba dancers. Section B will be the non-professional Samba dancers. Section C will represent the animal section, which will be divided into six groups according to the name of the animal costume they have. In the second half of rehearsal, everyone will rehearse as a group.",
          ]}
        />

        <DailyBlock
          pathPrefix="day.wed1"
          time="10:00 AM - 5:00 PM"
          location="Annunciation Recreation Center 800 Race St. New Orleans Louisiana 70130"
          image="/api/media/casa-samba/library/images/carnaval/Casa-Samba-Endymion-Parade-2025_19.jpg"
          imgAlt="Mestre Jorge Alabe, Mestre Curtis and Staff"
          caption="Mestre Jorge Alabe, Mestre Curtis and Staff — Director of the Bateria Bomani Pierre"
          heading="Wednesday 10 am to 5 pm Rehearsal Sessions"
          paragraphs={[
            "February 3 rehearsal",
            "Wednesday, from 10am to 12pm, introduction of the Samba instrument rehearsal session. The introduction rehearsal section is for everyone who is considered a beginner player. The introductory of instruments session will be on the percussion section, Plattinella, Tamborim, Reco-reco, Cuica, and Agogo (Agogo quartro boca), and then the drum section. Repequie and Caixa section, Repequie de More, and a Surdo section.",
            "NOTE: There will be two rehearsal sessions. One during the day for out-of-town visitors and one during the night for local people who work during the day. You can also attend both sessions. The Group rehearsal will be from 12pm to 5pm, and from 6 pm to 9 pm. There will be a 30-minute break between each rehearsal session. This will be an all-day section for everyone to rehearse together as a group, under the direction of Mestre Curtis Pierre and his Directors.",
            "The daytime 12:15pm to 5pm, and the night time sessions are from 6pm to 9pm the rehearsal section is for everyone: Bateria (drummers) and Passistas (dancers).",
          ]}
        />

        <DailyBlock
          pathPrefix="day.wed2"
          time="7:00 PM - 8:30 PM"
          location="Annunciation Recreation Center 800 Race St. New Orleans Louisiana 70130"
          image="/api/media/casa-samba/library/images/carnaval/Endymion-Parade_GOOGLEDOCS-pics_23.jpg"
          imgAlt="New Orleans Brass Band Musicians"
          caption="New Orleans Brass Band Musicians"
          heading="Wednesday Night Rehearsal Session"
          paragraphs={[
            "NOTE: There will be two rehearsal sessions. One during the day for out-of-town visitors and one during the night for local people who work during the day. You can also attend both sessions. The Group rehearsal will be from 12pm to 5pm, and from 6 pm to 9 pm. There will be a 30-minute break between each rehearsal session. This will be an all-day section for everyone to rehearse together as a group, under the direction of Mestre Curtis Pierre and his Directors.",
            "The daytime 12:15pm to 5pm, and the night time sessions are from 6pm to 9pm the rehearsal section is for everyone: Bateria (drummers) and Passistas (dancers). The drummers will be assigned to their instrument sections. Dancers will be divided into three groups: Samba Dancer sections A, B, and C. Section A will represent the professional Samba dancers. Section B will be the non-professional Samba dancers. Section C will represent the animal section, which will be divided into six groups according to the name of the animal costume they have. In the second half of rehearsal, everyone will rehearse as a group.",
            "Wednesday Night, February 3, extra practice for anyone (not mandatory), only if necessary - Special Class - Group sessions, private class, last-minute costume adjustments, instrument repair, and other last-minute preparation for both dancers and musicians",
          ]}
        />

        <DailyBlock
          pathPrefix="day.thu1"
          time="10:00 AM - 5:00 PM"
          location="Annunciation Recreation Center 800 Race St. New Orleans Louisiana 70130"
          image="/api/media/casa-samba/library/images/carnaval/Endymion-Parade_GOOGLEDOCS-pics_64-2-scaled.jpg"
          imgAlt="Mestre Curtis and Staff"
          caption="Mestre Curtis and Staff — Mandatory rehearsal 2"
          heading="Thursday 10 AM to 5 PM two rehearsal sessions"
          paragraphs={[
            "February 4th Mandatory rehearsal",
            "Thursday, from 10am to 12pm, introduction of the Samba instrument rehearsal session. The introduction rehearsal section is for everyone who is considered a beginner player. The introductory of instruments session will be on the percussion section, Plattinella, Tamborim, Reco-reco, Cuica, and Agogo (Agogo quartro boca), and then the drum section. Repequie and Caixa section, Repequie de More, and a Surdo section.",
            "NOTE: There will be two rehearsal sessions. One during the day for out-of-town visitors and one during the night for local people who work during the day. You can also attend both sessions. The Group rehearsal will be from 12pm to 5pm, and from 6 pm to 9 pm. There will be a 30-minute break between each rehearsal session. This will be an all-day section for everyone to rehearse together as a group, under the direction of Mestre Curtis Pierre and his Directors.",
          ]}
        />

        <DailyBlock
          pathPrefix="day.thu2"
          time="Evening"
          location="Annunciation Recreation Center 800 Race St. New Orleans Louisiana 70130"
          image="/api/media/casa-samba/library/images/carnaval/DSC_0121-scaled.jpg"
          imgAlt="Mestre Curtis Pierre"
          caption="Mestre Curtis Pierre — Special Class - Group sessions, private class last minute costume adjustments"
          heading="Thursday Night rehearsal sessions"
          paragraphs={[
            "From 6pm to 9pm the rehearsal section is for everyone Bateria (drummers) and Passistas (dancers). The drummers will be assigned to their instrument sections. Dancers will be divided into three groups: Samba Dancer sections A, B, and C. Section A will represent the professional Samba dancers. Section B will be the non-professional Samba dancers. Section C will represent the animal section, which will be divided into six groups according to the name of the animal costume they have. In the second half of rehearsal, everyone will rehearse as a group.",
            "Thursday Night - Special Class - Group sessions, private class, last-minute costume adjustments, instrument repair, and other last-minute preparation for both dancers and musicians",
          ]}
        />

        <DailyBlock
          pathPrefix="day.fri1"
          time="10:00 AM - 5:00 PM"
          location="Annunciation Recreation Center 800 Race St. New Orleans Louisiana 70130"
          image="/api/media/casa-samba/library/images/carnaval/Casa-Samba-Endymion-Parade-2025_20.jpg"
          imgAlt="Mestre Jorge Alabe, Mestre Curtis and Staff"
          caption="Mestre Jorge Alabe, Mestre Curtis and Staff — Final Mandatory rehearsal 3"
          heading="Friday 10 AM to 5 PM two rehearsal sessions"
          paragraphs={[
            "February 5 Mandatory rehearsal. Friday is a mandatory rehearsal session from 10 A.M. to 5 P.M. Mandatory rehearsal session. This rehearsal section is mandatory for everyone registered for the conference. From 10am to 12pm, introduction of the Samba instrument rehearsal session. The introduction rehearsal section is for everyone who is considered a beginner player. The introductory of instruments session will be on the percussion section, Plattinella, Tamborim, Reco-reco, Cuica, and Agogo (Agogo quartro boca), and then the drum section. Repequie and Caixa section, Repequie de More, and a Surdo section.",
            "NOTE: There will be two rehearsal sessions. One during the day for out-of-town visitors and one during the night for local people who work during the day. You can also attend both sessions. The Group rehearsal will be during the day from 12pm to 5pm, and from 6 pm to 9 pm. There will be a 30-minute break between each rehearsal session. This will be an all-day section for everyone to rehearse together as a group, under the direction of Mestre Curtis Pierre and his Directors.",
          ]}
        />

        <DailyBlock
          pathPrefix="day.fri2"
          time="Evening"
          location="Annunciation Recreation Center 800 Race St. New Orleans Louisiana 70130"
          image="/api/media/casa-samba/library/images/carnaval/Casa-Samba-Endymion-Parade-2025_20.jpg"
          imgAlt="Mestre Jorge Alabe, Mestre Curtis and Staff"
          caption="Mestre Jorge Alabe, Mestre Curtis and Staff — Final Mandatory rehearsal 3"
          heading="Friday Night 6 PM to 9 PM Rehearsal Sessions"
          paragraphs={[
            "February 5 Mandatory rehearsal. Thursday from 10 A.M. to 5 P.M. Mandatory rehearsal session. This rehearsal section is mandatory for everyone registered for the conference. This will be an all-day section for everyone to rehearse together as a group, under the direction of Mestre Curtis Pierre and his Directors. The rehearsal will be from 10 A.M. to 4 P.M. There will be a 30-minute break between the 2 rehearsal sessions.",
            "From 10am to 12pm, introduction of the Samba instrument rehearsal session. The introduction rehearsal section is for everyone who is considered a beginner player. The introductory of instruments session will be on the percussion section, Plattinella, Tamborim, Reco-reco, Cuica, and Agogo (Agogo quartro boca), and then the drum section. Repequie and Caixa section, Repequie de More, and a Surdo section.",
          ]}
        />

        <DailyBlock
          pathPrefix="day.sat"
          time="10:00 AM - 2:00 PM"
          location="Annunciation Recreation Center 800 Race St. New Orleans Louisiana 70130"
          image="/api/media/casa-samba/library/images/carnaval/Casa-Samba-Endymion-Parade-2025_12.jpg"
          imgAlt="Saturday Endymion Parade Lineup 2 PM"
          caption="Saturday Endymion Parade Lineup 2 PM — Saturday: 2:00 PM Meet at the designated parade location"
          heading="Saturday Endymion Parade Location Lineup 2 PM"
          paragraphs={[
            "February 6 Endymion Parade Day",
            "There will be a bus supply for transportation. If you are not riding the bus, you should meet on Saturday at 2:30 PM at the designated parade location. The Endymion Parade Lines up at Marconi drive and city Park Drive. Locate it at New Orleans City Park. As a parade unit, we should meet 2 1/2 hours before the starting time of the parade. Our location within the Endymion Parade will be announced at the final rehearsal.",
            "You will be responsible for your transportation to and from the parade and rehearsals.",
            "Saturday Endymion Parade Location Lineup 2 PM near Marconi Drive in City Park Avenue, we will be located behind the Grand Marshal or TBA, and will begin arriving at 2 o'clock at (5:00 PM Endymion parade will begin. The route from City Park to the Superdome will be: City Park Drive to Carrollton Avenue, turn right on Carrollton Avenue, and in turn left on Canal Street. Continue on Canal Street to the downtown area and turn right on Saint Charles Avenue we will then go to Podyras Street and finish at the New Orleans Superdome.",
            "There will be at least 1 one to 2 two Casa Samba facilitators for each of the 3 dance sections.",
            "The Samba percussion band (Bateria) received Instruments from the equipment truck.",
            "No one will be allowed to parade without a costume or a Casa Samba support T-shirt.",
          ]}
        />
      </section>

      {/* Pricing */}
      <section className="max-w-6xl mx-auto px-6 py-16" data-testid="pricing-section">
        <SectionHeading>
          <PageEditable path="pricing.heading" as="span" multiline fallback="Purchase your Mardi Gras Brazil Carnaval Conference 2027 position" />
        </SectionHeading>
        <PageEditable path="pricing.subheading" as="p" multiline className="text-muted-foreground mb-8" fallback="Flexible pricing with no surprises" />

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {PRICING.map((p, i) => (
            <div
              key={p.title}
              className={`rounded-2xl p-6 border ${p.featured ? 'border-primary bg-primary/5' : 'border-border/50 bg-card'}`}
              data-testid={`pricing-card-${i}`}
            >
              {p.featured && (
                <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-primary text-black text-xs font-bold uppercase tracking-wider mb-3">
                  <Star className="w-3 h-3" /> <PageEditable path={`pricing.${i}.badge`} as="span" fallback="Most Popular" />
                </div>
              )}
              <PageEditable path={`pricing.${i}.title`} as="h3" multiline className="text-xl font-bold text-foreground mb-2" fallback={p.title} />
              <PageEditable path={`pricing.${i}.price`} as="div" className="text-3xl font-bold font-display text-primary mb-4" fallback={p.price} />
              <ul className="space-y-2 mb-6 text-sm text-muted-foreground">
                {p.bullets.map((b, bi) => (
                  <li key={bi} className="flex gap-2">
                    <ChevronRight className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                    <PageEditable path={`pricing.${i}.bullets.${bi}`} as="span" multiline fallback={b} />
                  </li>
                ))}
              </ul>
              <a href={REGISTER_URL} target="_blank" rel="noopener noreferrer"
                className={`block text-center px-4 py-2 rounded-full font-semibold transition-opacity ${
                  p.featured
                    ? 'bg-primary text-black hover:opacity-90'
                    : 'border border-primary text-primary hover:bg-primary/10'
                }`}>
                <PageEditable path={`pricing.${i}.cta`} as="span" fallback="Register Here" />
              </a>
            </div>
          ))}
        </div>

        <p className="text-muted-foreground mt-8">
          <PageEditable path="pricing.footerPrefix" as="span" fallback="Need additional pricing info?" />{' '}
          <Link to="/contact" className="text-primary hover:underline">
            <PageEditable path="pricing.footerLink" as="span" fallback="Get in touch" />
          </Link>
        </p>
      </section>

      {/* Register Now callout */}
      <section className="max-w-5xl mx-auto px-6 py-12">
        <div className="rounded-2xl bg-card border border-border/50 p-8">
          <PageEditable
            path="regNow.heading"
            as="h3"
            multiline
            className="text-2xl md:text-3xl font-bold font-display text-foreground mb-4"
            fallback="Register for Mardi Gras Brazil Carnaval Conference 2027"
          />
          <PageEditable
            path="regNow.body"
            as="p"
            multiline
            className="text-muted-foreground leading-relaxed mb-6"
            fallback="Lock into Mardi Gras Brazil Carnaval Conference 2027. A limited number of positions for all categories professional dancers 25, non-professional dancers 25, Drummers (Bateria) 60. Drummer's costumes will be handmade by Mestre Curtis and Casa Samba are now available for $350 but only 60 people. The animal section is limited to 200 people. We have a strict limit of 300 people that can participate with in the Endymion parade with Casa Samba. Pre-register now and we'll contact you with more details when it's time to complete your registration"
          />
          <a href={REGISTER_URL} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-black font-bold hover:opacity-90 transition-opacity"
            data-testid="register-now-btn">
            <PageEditable path="regNow.cta" as="span" fallback="REGISTER NOW" /> <ChevronRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      {/* Testimonials */}
      <section className="max-w-6xl mx-auto px-6 py-12">
        <SectionHeading>
          <PageEditable path="testimonials.heading" as="span" multiline fallback="What people are saying about the First Mardi Gras Brazil Carnaval Conference 2027" />
        </SectionHeading>
        <div className="grid md:grid-cols-2 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <div key={i} className="bg-card border border-border/50 rounded-2xl p-6" data-testid={`testimonial-${i}`}>
              <PageEditable path={`testimonials.${i}.quote`} as="p" multiline className="text-muted-foreground italic leading-relaxed mb-4" fallback={`"${t.quote}"`} />
              <div className="flex items-center gap-3">
              <PageEditableImage path={`testimonials.${i}.photo`} fallback={t.photo} alt={t.name} className="w-12 h-12 rounded-full object-cover border border-border/50" />
                <div>
                  <PageEditable path={`testimonials.${i}.name`} as="div" className="font-semibold text-foreground" fallback={t.name} />
                  <PageEditable path={`testimonials.${i}.role`} as="div" multiline className="text-xs text-muted-foreground" fallback={t.role} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Partnership */}
      <section className="max-w-5xl mx-auto px-6 py-12">
        <SectionHeading>
          <PageEditable path="partnership.heading" as="span" multiline fallback="Latest News: partnership establish by Casa Samba and Academico Escola de Samba Salgueiro." />
        </SectionHeading>
        <PageEditable
          path="partnership.body"
          as="p"
          multiline
          className="text-muted-foreground leading-relaxed mb-4"
          fallback="CREATION OF THE EMBASSY OF THE GRÊMIO RECREATIVO ESCOLA DE SAMBA ACADÊMICOS DO SALGUEIRO IN INTERNATIONAL TERRITORY Escola de Samba Casa Samba ( ano de criação) Introduction The Grêmio Recreativo Escola de Samba Acadêmicos do Salgueiro was founded on March 5, 1953 and is one of the most important carnival groups in Rio de Janeiro. The school is one of the most traditional, having won nine titles in the Special Group. Salgueiro is a samba school with a strong sense of community and a rich history. The school has become a symbol of cultural resistance, innovation and creativity by raising the profile of black protagonists and characters read more...."
        />
        <a href="https://www.casasamba.com/Registration/partnership/" target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-primary text-primary hover:bg-primary/10 transition-colors">
          <PageEditable path="partnership.cta" as="span" fallback="Read More About The Partnership" /> <ExternalLink className="w-4 h-4" />
        </a>
      </section>

      {/* Sponsors */}
      <section className="max-w-6xl mx-auto px-6 py-12">
        <SectionHeading>
          <PageEditable path="sponsors.heading" as="span" fallback="Partners/Sponsors" />
        </SectionHeading>
        <PageEditable
          path="sponsors.intro"
          as="p"
          multiline
          className="text-muted-foreground mb-8"
          fallback="A huge thanks to all our amazing partners. We couldn't have a conference without you!"
        />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 items-center">
          {SPONSORS.map((s, si) => (
            <div key={s.name} className="bg-card border border-border/50 rounded-xl p-4 flex items-center justify-center h-24">
              <PageEditableImage path={`sponsors.${si}.src`} fallback={s.src} alt={s.name} className="max-h-16 max-w-full object-contain" />
            </div>
          ))}
        </div>

        <div className="mt-10 bg-card border border-border/50 rounded-2xl p-6">
          <PageEditable path="sponsors.cta.heading" as="h3" multiline className="text-xl font-bold text-foreground mb-3" fallback="Interested in becoming a sponsor?" />
          <PageEditable
            path="sponsors.cta.body"
            as="p"
            multiline
            className="text-muted-foreground mb-4"
            fallback="If your company is interested in sponsoring Mardi Gras Brazil Carnaval Conference 2027 this year, we'd love to hear from you. Send us a quick email and we'll get right back to you with some more information and our sponsorship brochure"
          />
          <Link to="/contact" className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-primary text-black font-semibold hover:opacity-90 transition-opacity">
            <PageEditable path="sponsors.cta.btn" as="span" fallback="Request Now" /> <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Location */}
      <section className="max-w-5xl mx-auto px-6 py-12">
        <SectionHeading>
          <PageEditable path="location.heading" as="span" multiline fallback="Possible location Gernon Brown Recreation Center" />
        </SectionHeading>
        <PageEditable path="location.sub1" as="p" multiline className="text-muted-foreground mb-2" fallback="Possible Location that the conference will be held. This location is subject to change." />
        <PageEditable path="location.sub2" as="p" multiline className="text-muted-foreground mb-6" fallback="This is a possible location. If the location is change you will be sent an email on the new address and location" />

        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-card border border-border/50 rounded-2xl p-6">
            <div className="flex items-start gap-3 mb-4">
              <MapPin className="w-6 h-6 text-primary mt-1" />
              <div>
                <PageEditable path="location.venueName" as="div" className="font-bold text-foreground" fallback="800 Race Annunciation Recreation Center" />
                <PageEditable path="location.venueNote" as="div" multiline className="text-muted-foreground text-sm" fallback="This is a New Orleans recreation center it is a very secure area." />
              </div>
            </div>
            <div className="text-sm text-muted-foreground">
              <div><PageEditable path="location.phoneLabel" as="span" fallback="Phone:" /> <PageEditable path="location.phone" as="strong" className="text-foreground" fallback="504-236-7479" /></div>
              <div><PageEditable path="location.phone2Label" as="span" fallback="Location phone number:" /> <PageEditable path="location.phone2" as="strong" className="text-foreground" fallback="504-658-3095" /></div>
            </div>
          </div>

          <div className="bg-card border border-border/50 rounded-2xl p-6 space-y-4 text-sm text-muted-foreground">
            <div>
              <PageEditable path="location.transport.title" as="h4" className="font-bold text-foreground mb-1" fallback="Transport" />
              <PageEditable path="location.transport.body" as="p" multiline fallback="Our venue is a 14-minute drive from downtown. Street-car railway runs from the downtown CBD to the city park. The trolley car makes frequent runs on this route." />
            </div>
            <div>
              <PageEditable path="location.parking.title" as="h4" className="font-bold text-foreground mb-1" fallback="Parking" />
              <PageEditable path="location.parking.body" as="p" multiline fallback="Parking can be tricky and expensive in New Orleans. Make sure you or not in a two away soon. Because of the high amount of parking space needed, you will be towed away immediately." />
            </div>
            <div>
              <PageEditable path="location.stay.title" as="h4" className="font-bold text-foreground mb-1" fallback="Places To Stay" />
              <PageEditable path="location.stay.body" as="p" multiline fallback="Make the most of your visit and stay the night. If you want to be super near the conference venue then you would be subject to a high traffic area for going in and out during the week Carnaval. I suggest staying in the outer then that's wise uptown further out of the city lower the prices. Options or Metairie, Kenner, or New Orleans West Bank. You will have to cross the bridge but the BrB & hotel prices are much cheaper." />
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <div className="rounded-2xl bg-gradient-to-r from-orange-600/30 via-red-600/30 to-yellow-600/30 border-2 border-primary p-8 text-center">
          <PageEditable
            path="finalCta.heading"
            as="h3"
            multiline
            className="text-2xl md:text-3xl font-bold font-display text-foreground mb-4"
            fallback="Register today for the Mardi Gras Brasil Carnaval Conference now"
          />
          <PageEditable
            path="finalCta.body"
            as="p"
            multiline
            className="text-muted-foreground mb-6"
            fallback="Lock in the lowest price for Mardi Gras Brazil Carnaval Conference 2027. Pre-register now and we'll contact you with more details when it's time to complete your registration"
          />
          <a href={REGISTER_URL} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-primary text-black text-lg font-bold hover:opacity-90 transition-opacity"
            data-testid="final-cta-btn">
            <PageEditable path="finalCta.cta" as="span" fallback="Reserve My Spot" /> <ChevronRight className="w-5 h-5" />
          </a>
        </div>
      </section>
      {/* Sticky mobile CTA */}
      <div className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-black/85 backdrop-blur-md border-t border-orange-500/40 p-3">
        <a
          href="#register-form"
          className="flex items-center justify-center gap-2 w-full px-6 py-3 rounded-full bg-gradient-to-r from-orange-500 via-red-500 to-yellow-500 text-white font-bold shadow-lg"
          data-testid="mobile-sticky-cta"
        >
          <Flame className="w-4 h-4" />
          <PageEditable path="mobileCta.label" as="span" fallback="Claim my parade spot" />
          <ChevronRight className="w-4 h-4" />
        </a>
      </div>
    </div>
  );
};

// ---------- helpers ----------
const DailyBlock = ({ pathPrefix, time, location, image, imgAlt, caption, heading, paragraphs = [], children }) => (
  <div className="bg-card border border-border/50 rounded-2xl overflow-hidden">
    <div className="grid md:grid-cols-[2fr_3fr] gap-0">
      {image ? (
        <div className="relative">
          <PageEditableImage path={`${pathPrefix}.image`} fallback={image} alt={imgAlt || ''} className="w-full h-full object-cover min-h-[240px]" loading="lazy" />
        </div>
      ) : null}
      <div className="p-6">
        <div className="flex items-center gap-2 text-primary text-sm font-semibold mb-2">
          <Calendar className="w-4 h-4" />{' '}
          <PageEditable path={`${pathPrefix}.time`} as="span" fallback={time} />
        </div>
        <div className="flex items-center gap-2 text-muted-foreground text-xs mb-4">
          <MapPin className="w-3 h-3" />{' '}
          <PageEditable path={`${pathPrefix}.location`} as="span" multiline fallback={location} />
        </div>
        {caption && (
          <PageEditable path={`${pathPrefix}.caption`} as="div" multiline className="text-sm text-muted-foreground mb-3 italic" fallback={caption} />
        )}
        <PageEditable path={`${pathPrefix}.heading`} as="h3" multiline className="text-xl md:text-2xl font-bold text-foreground mb-4" fallback={heading} />
        <div className="space-y-3 text-muted-foreground text-sm leading-relaxed">
          {paragraphs.length > 0
            ? paragraphs.map((p, i) => (
                <PageEditable
                  key={i}
                  path={`${pathPrefix}.p${i + 1}`}
                  as="p"
                  multiline
                  fallback={p}
                />
              ))
            : children}
        </div>
      </div>
    </div>
  </div>
);

export default Registration;
